
// Fellow setup page: builder, setups, café math, sticky bar and cart drawer.
// On rarely.co ("shopify" mode) prices/availability come from /products/<handle>.js and the cart is the real AJAX cart.
// Anywhere else ("local" mode) the cart lives in localStorage and checkout opens a rarely.co cart permalink.
(() => {
  'use strict';
  const CFG = JSON.parse(document.getElementById('fs-config').textContent);
  const SHOP = CFG.mode === 'shopify' || (CFG.mode !== 'local' && /(^|\.)rarely\.co$|\.myshopify\.com$/.test(location.hostname));
  const ROOT = CFG.root || '/';
  const STORE = 'https://rarely.co/';
  const FREE = Number(CFG.freeShip) || 120;
  const IMG = CFG.img || 'assets/img/cut/';
  const WALNUT = 'linear-gradient(135deg,#1E1E1E 0 50%,#7A4B2A 50% 100%)';

  const P = {
    opus:   { step:'Grind', name:'Opus 2 Burr Grinder', plural:'Opus 2 grinders', handle:'opus-2-conical-burr-grinder-48mm-burrs-stepless-adjustment', v:[
              { id:44486896943203, label:'Black', price:199.95, img:'opus' } ] },
    stagg:  { step:'Heat', name:'Stagg EKG Pro Kettle', plural:'EKG Pro kettles', handle:'stagg-ekg-electric-kettle-variable-temp-104-212-f-30-oz', legend:'Handle', kind:'seg', v:[
              { id:44486898516067, label:'Black', price:179.95, img:'stagg-black', sw:'#1E1E1E' },
              { id:44486898548835, label:'Walnut', price:199.95, img:'stagg-walnut', sw:WALNUT } ] },
    clara:  { step:'Brew', name:'Clara French Press', plural:'Clara presses', handle:'clara-french-press-24-oz-vacuum-insulated-stainless-steel', v:[
              { id:44486897598563, label:'Matte black', price:99.95, img:'clara' } ] },
    pirch:  { step:'Sip', name:'Pirch Glasses, set of 2', plural:'Pirch sets', unit:'set', small:true, handle:'pirch-espresso-glasses-soda-lime-glass-set-of-2', legend:'Tint', kind:'dot', v:[
              { id:44486899630179, label:'Amber', price:44.95, img:'pirch-amber', sw:'#B4722C' },
              { id:44486899564643, label:'Smoke', price:44.95, img:'pirch-smoke', sw:'#8A8883' },
              { id:44486899597411, label:'Garnet', price:44.95, img:'pirch-garnet', sw:'#8E2A4B' },
              { id:44486899662947, label:'Woodland', price:44.95, img:'pirch-woodland', sw:'#5E7A55' } ] },
    carter: { step:'Go', name:'Carter Move Mug, 12 oz', plural:'Carter Move mugs', small:true, handle:'carter-move-mug-true-taste-ceramic-interior-12-oz', v:[
              { id:44486899269731, label:'Matte black', price:30.00, img:'carter' } ] }
  };
  const ORDER = ['opus','stagg','clara','pirch','carter'];
  const SETUPS = {
    complete:   { keys:['opus','stagg','clara','pirch','carter'] },
    daily:      { keys:['opus','stagg','clara'] },
    essentials: { keys:['clara','carter'] }
  };
  const FEATURE = {
    grinder: { k:'opus',   h1:'Fresh grounds.<br> <span>Dialed to your brew.</span>', lede:'The Opus 2 goes from espresso-fine to cold-brew coarse on one stepless dial. Add the kettle and press below and every step from bean to cup is covered.' },
    kettle:  { k:'stagg',  h1:'The exact degree.<br> <span>Every single pour.</span>', lede:'The Stagg EKG Pro holds any temperature from 104–212°F and pours through a precision gooseneck. Add the grinder and press below for the full setup.' },
    press:   { k:'clara',  h1:'Stays hot.<br> <span>Won’t shatter.</span>', lede:'Clara is vacuum-insulated stainless steel with finer mesh filtration. Add fresh grounds and precise water below for the full setup.' },
    glasses: { k:'pirch',  h1:'Your latte art<br> <span>deserves this glass.</span>', lede:'Pirch glasses have curved interiors designed for latte art, in four tints. Build the rest of your setup below, all in one checkout.' },
    mug:     { k:'carter', h1:'Hot at noon.<br> <span>Tastes like coffee.</span>', lede:'The Carter Move has a ceramic interior, 12-hour insulation and a leak-proof lid. Build the rest of your setup below, all in one checkout.' }
  };
  ['opus','stagg','clara','pirch','carter'].forEach(k => { FEATURE[k] = FEATURE[Object.keys(FEATURE).find(f => FEATURE[f].k === k)]; });

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
  const money = n => Number.isFinite(n) ? '$' + n.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 }) : '—';
  const whole = n => '$' + Math.round(n).toLocaleString('en-US');
  const imgOf = v => CFG.images[v.img];
  const pdp = k => (SHOP ? ROOT : STORE) + 'products/' + P[k].handle;
  const byId = new Map();
  ORDER.forEach(k => P[k].v.forEach((v, i) => byId.set(v.id, { k, i, v })));

  // ---------- state ----------
  const state = { sel:{}, picks:new Set(ORDER), featured:null };
  ORDER.forEach(k => { state.sel[k] = 0; });
  const variant = k => P[k].v[state.sel[k]];

  // ---------- stock + availability ----------
  const soldOut = new Set();
  let catalogReady = false;
  const stockOf = v => { const q = CFG.stock && CFG.stock[String(v.id)]; return Number.isFinite(q) ? q : null; };
  const vAvail = v => catalogReady && Number.isFinite(v.price) && !soldOut.has(v.id) && stockOf(v) !== 0;
  const avail = k => vAvail(variant(k));
  function stockText(k) {
    const v = variant(k), q = stockOf(v);
    if (!catalogReady) return ['', ''];
    if (!vAvail(v)) return ['Sold out', 'out'];
    if (q === 1) return [P[k].unit === 'set' ? 'Last set in ' + v.label : 'Last one', 'low'];
    if (q !== null && q <= 5) return ['Only ' + q + ' left', 'low'];
    return ['In stock', ''];
  }
  // If the chosen variant sells out, move to the first one still available.
  function settleVariants() {
    ORDER.forEach(k => { if (!avail(k)) { const i = P[k].v.findIndex(vAvail); if (i >= 0) state.sel[k] = i; } });
  }

  const picked = () => ORDER.filter(k => state.picks.has(k) && avail(k));
  const sum = keys => keys.reduce((n, k) => n + (variant(k).price || 0), 0);

  async function request(url, options = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 12000);
    try {
      const res = await fetch(url, { credentials:'same-origin', ...options, signal:ctl.signal });
      let data = null;
      try { data = await res.json(); } catch (e) {}
      return { ok:res.ok, status:res.status, data };
    } finally { clearTimeout(timer); }
  }

  async function loadCatalog() {
    if (!SHOP) return;
    catalogReady = false; render();
    $('[data-catalog-status]').textContent = 'Checking live prices and availability…';
    $('[data-retry-catalog]').hidden = true;
    try {
      const products = await Promise.all(ORDER.map(async k => {
        const r = await request(`${ROOT}products/${P[k].handle}.js`, { cache:'no-store', headers:{ Accept:'application/json' } });
        if (!r.ok || !r.data || !Array.isArray(r.data.variants)) throw new Error('catalog');
        return r.data;
      }));
      soldOut.clear();
      products.forEach((product, i) => P[ORDER[i]].v.forEach(v => {
        const live = product.variants.find(c => String(c.id) === String(v.id));
        if (!live || !Number.isSafeInteger(live.price)) { soldOut.add(v.id); return; }
        v.price = live.price / 100;
        if (live.available !== true) soldOut.add(v.id);
      }));
      catalogReady = true;
      $('[data-catalog-status]').textContent = '';
    } catch (e) {
      catalogReady = false;
      $('[data-catalog-status]').textContent = 'Live availability could not be checked. Please retry before adding items.';
      $('[data-retry-catalog]').hidden = false;
    }
    settleVariants();
    render();
  }

  // ---------- variant pickers ----------
  let optSeq = 0;
  function buildOpts() {
    $$('[data-opts]').forEach(slot => {
      const k = slot.dataset.opts, p = P[k], n = optSeq++;
      const fs = document.createElement('fieldset');
      fs.className = 'opts' + (p.kind === 'seg' ? ' is-seg' : '');
      const base = p.v[0].price;
      fs.innerHTML = `<legend>${p.legend}: <b data-opt-label="${k}"></b></legend>` + p.v.map((v, j) => {
        if (p.kind === 'seg') {
          const extra = v.price > base ? ` +${whole(v.price - base)}` : '';
          return `<label class="seg"><input type="radio" name="opt-${k}-${n}" value="${j}" data-opt="${k}"><span><i style="background:${v.sw}"></i>${esc(v.label)}${extra}</span></label>`;
        }
        return `<label class="dotsw" title="${esc(v.label)}"><input type="radio" name="opt-${k}-${n}" value="${j}" data-opt="${k}" aria-label="${esc(v.label)}"><i style="background:${v.sw}"></i></label>`;
      }).join('');
      slot.replaceWith(fs);
    });
  }

  // ---------- message match (?item=kettle etc.) ----------
  function applyFeature() {
    const q = new URLSearchParams(location.search);
    const key = (q.get('item') || q.get('product') || '').toLowerCase();
    const f = FEATURE[key];
    if (!f) return;
    state.featured = f.k;
    state.picks = new Set([f.k]);
    $('[data-h1]').innerHTML = f.h1;
    $('[data-lede]').textContent = f.lede;
    $('[data-build-hint]').textContent = 'Your pick is in. Add the rest of the setup below.';
    const row = $(`[data-row="${f.k}"]`);
    row.classList.add('featured');
    $('.b-name', row).insertAdjacentHTML('beforeend', '<span class="feat-tag">Your pick</span>');
    $('[data-blist]').prepend(row);
  }

  // ---------- render ----------
  function shipHTML(total, keys) {
    if (total <= 0) return { cls:'', html:'Pick at least one piece to see your total.' };
    if (total >= FREE) return { cls:'free', html:'<svg aria-hidden="true"><use href="#i-truck"/></svg>' + (keys.length > 1 ? 'Free shipping on this setup' : 'Free shipping included') };
    const need = FREE - total;
    const options = ORDER.filter(k => !keys.includes(k) && avail(k)).sort((a, b) => variant(a).price - variant(b).price);
    const fill = options.find(k => variant(k).price >= need) || options[options.length - 1];
    let html = `<svg aria-hidden="true"><use href="#i-truck"/></svg><span>Add <b class="num">${money(need)}</b> for free shipping.</span>`;
    if (fill) html += ` <button type="button" class="ship-add" data-pick-add="${fill}">Add the ${esc(P[fill].name.split(',')[0])} (${money(variant(fill).price)})</button>`;
    return { cls:'', html };
  }

  function lineupHTML(keys, dimOthers) {
    return ORDER.filter(k => dimOthers || keys.includes(k)).map(k =>
      `<img src="${imgOf(variant(k))}" alt="" class="${P[k].small ? 'sm' : ''} ${keys.includes(k) ? '' : 'off'}" width="80" height="80" loading="lazy">`
    ).join('');
  }

  function render() {
    // prices, stock, thumbs, options
    ORDER.forEach(k => {
      const v = variant(k), [txt, cls] = stockText(k);
      $$(`[data-price="${k}"]`).forEach(el => { el.textContent = money(v.price); });
      $$(`[data-stock="${k}"]`).forEach(el => { el.textContent = txt; el.className = 'stock ' + cls + (el.classList.contains('m-only') ? ' m-only' : ''); el.hidden = !txt; });
      $$(`[data-thumb="${k}"]`).forEach(el => { if (!el.src.endsWith(imgOf(v))) el.src = imgOf(v); });
      $$(`[data-opt-label="${k}"]`).forEach(el => { el.textContent = v.label; });
      $$(`input[data-opt="${k}"]`).forEach(inp => {
        inp.checked = +inp.value === state.sel[k];
        inp.disabled = catalogReady && !vAvail(P[k].v[+inp.value]);
      });
      $$(`[data-pick="${k}"]`).forEach(cb => {
        cb.checked = state.picks.has(k) && avail(k);
        cb.disabled = !avail(k);
        cb.closest('.b-row').classList.toggle('off', !cb.checked);
      });
      $$(`[data-add="${k}"]`).forEach(b => {
        if (b.classList.contains('is-added')) return;
        b.disabled = busy || !avail(k);
        b.textContent = avail(k) || !catalogReady ? 'Add to cart' : 'Sold out';
      });
      $$(`[data-pdp="${k}"]`).forEach(a => { a.href = pdp(k); });
    });

    // builder totals
    const keys = picked(), total = sum(keys), count = keys.length;
    $$('[data-count]').forEach(el => { el.textContent = count; });
    $$('[data-total]').forEach(el => { el.textContent = money(total); });
    const label = count === 0 ? 'Choose a piece to start' : count === 1 ? 'Add to cart' : count === ORDER.length ? 'Add setup to cart' : `Add ${count} pieces to cart`;
    $$('[data-add-picked]').forEach(b => {
      if (b.classList.contains('is-added')) return;
      b.disabled = busy || count === 0;
      const l = $('[data-cta-label]', b); if (l) l.textContent = label;
    });
    const ship = shipHTML(total, keys);
    $$('[data-ship]').forEach(el => { el.className = 'ship ' + ship.cls; el.innerHTML = ship.html; });
    const lu = $('[data-lineup]'); if (lu) lu.innerHTML = lineupHTML(keys, true);
    const st = $('[data-st-thumbs]'); if (st) st.innerHTML = lineupHTML(keys, false);
    const stShip = $('[data-st-ship]');
    if (stShip) { const free = total >= FREE; stShip.textContent = total <= 0 ? '' : free ? 'Free shipping' : `${money(FREE - total)} from free shipping`; stShip.classList.toggle('need', !free); }
    $('[data-st-title]').textContent = count === 1 ? P[keys[0]].name : count === ORDER.length ? 'The complete setup' : 'Your setup';

    // setups
    Object.entries(SETUPS).forEach(([id, s]) => {
      const card = $(`[data-setup="${id}"]`); if (!card) return;
      const ok = s.keys.every(avail), t = sum(s.keys);
      $('[data-setup-price]', card).textContent = money(t);
      $('[data-setup-imgs]', card).innerHTML = s.keys.map(k => `<img src="${imgOf(variant(k))}" alt="" class="${P[k].small ? 'sm' : ''}" width="90" height="90" loading="lazy">`).join('');
      $('[data-setup-list]', card).innerHTML = s.keys.map(k => `<li><span>${esc(P[k].name)}${P[k].v.length > 1 ? ' · ' + esc(variant(k).label) : ''}</span><span class="num">${money(variant(k).price)}</span></li>`).join('');
      $('[data-setup-perk]', card).textContent = id === 'complete'
        ? `Everything in Daily Brewer, plus glasses and a travel mug for ${money(t - sum(SETUPS.daily.keys))} more`
        : t >= FREE ? 'Free shipping included' : `${money(FREE - t)} from free shipping`;
      const btn = $('[data-add-setup]', card);
      if (!btn.classList.contains('is-added')) btn.disabled = busy || !ok;
    });

    // scarcity line (genuine low stock only)
    const low = ORDER.map(k => {
      const qs = P[k].v.map(stockOf).filter(q => q !== null);
      return { k, q: qs.length ? qs.reduce((a, b) => a + b, 0) : null, perVariant: P[k].v.length > 1 && qs.every(q => q === 1) };
    }).filter(x => x.q !== null && x.q > 0 && x.q <= 5).sort((a, b) => a.q - b.q);
    const sc = $('[data-scarce]');
    if (sc) {
      const parts = low.slice(0, 3).map(x => x.perVariant ? `one ${P[x.k].plural.replace(/s$/, '')} per ${P[x.k].legend.toLowerCase()}` : `${x.q} ${P[x.k].plural}`);
      sc.hidden = !parts.length;
      if (parts.length) $('span', sc).textContent = 'Limited stock: ' + parts.slice(0, -1).join(', ') + (parts.length > 1 ? ' and ' : '') + parts[parts.length - 1] + ' left.';
    }

    renderMath(total);
    renderCart();
  }

  // ---------- café math ----------
  const mDrinks = $('#m-drinks'), mPrice = $('#m-price');
  function renderMath(total) {
    const d = +mDrinks.value, p = +mPrice.value;
    $('#o-drinks').textContent = d;
    $('#o-price').textContent = money(p);
    [mDrinks, mPrice].forEach(r => r.style.setProperty('--fill', ((r.value - r.min) / (r.max - r.min) * 100) + '%'));
    const year = d * p * 52, home = d * 1 * 52, weeklySave = d * (p - 1);
    $('[data-m-year]').textContent = whole(year);
    $('[data-m-home]').textContent = whole(home);
    const weeks = total > 0 && weeklySave > 0 ? Math.ceil(total / weeklySave) : null;
    $('[data-m-pay]').textContent = weeks === null ? '—' : weeks <= 1 ? '1 week' : weeks + ' weeks';
  }
  [mDrinks, mPrice].forEach(r => r.addEventListener('input', () => renderMath(sum(picked()))));

  // ---------- cart ----------
  const LOCAL_CART = 'rarely-fellow-setup-cart';
  let cart = { lines:[], count:0, subtotal:0 };
  let busy = false;

  function fromShopify(raw) {
    const lines = (raw.items || []).map(it => {
      const id = Number(it.variant_id || it.id), own = byId.get(id);
      return {
        key:it.key, id, qty:it.quantity,
        title: own ? P[own.k].name : it.product_title,
        variant: own ? (P[own.k].v.length > 1 ? own.v.label : '') : (it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : ''),
        line: it.final_line_price / 100,
        image: own ? imgOf(own.v) : (it.image ? it.image + (it.image.includes('?') ? '&' : '?') + 'width=160' : ''),
        max: own ? stockOf(own.v) : null
      };
    });
    return { lines, count:raw.item_count || 0, subtotal:(raw.total_price || 0) / 100 };
  }
  function readLocal() {
    try { const d = JSON.parse(localStorage.getItem(LOCAL_CART) || '[]'); return Array.isArray(d) ? d.filter(l => byId.has(l.id) && l.q > 0) : []; }
    catch (e) { return []; }
  }
  function writeLocal(lines) { try { localStorage.setItem(LOCAL_CART, JSON.stringify(lines)); } catch (e) {} }
  function fromLocal(lines) {
    const out = lines.map(l => {
      const { k, v } = byId.get(l.id);
      return { key:String(l.id), id:l.id, qty:l.q, title:P[k].name, variant:P[k].v.length > 1 ? v.label : '', line:v.price * l.q, image:imgOf(v), max:stockOf(v) };
    });
    return { lines:out, count:out.reduce((n, l) => n + l.qty, 0), subtotal:out.reduce((n, l) => n + l.line, 0) };
  }
  async function refreshCart() {
    if (!SHOP) { cart = fromLocal(readLocal()); renderCart(); return; }
    const r = await request(ROOT + 'cart.js', { cache:'no-store', headers:{ Accept:'application/json' } });
    if (!r.ok || !r.data) throw new Error('cart');
    cart = fromShopify(r.data); renderCart();
  }
  const inCartQty = id => cart.lines.filter(l => l.id === id).reduce((n, l) => n + l.qty, 0);

  // Never retries a POST whose outcome is unknown; it re-reads the cart and says so instead.
  async function addItems(items, btn) {
    if (busy || !catalogReady || !items.length || items.some(i => !vAvail(byId.get(i.id).v))) return;
    const capped = items.filter(i => { const q = stockOf(byId.get(i.id).v); return q === null || inCartQty(i.id) + i.quantity <= q; });
    if (capped.length < items.length) {
      const names = items.filter(i => !capped.includes(i)).map(i => P[byId.get(i.id).k].name.split(',')[0]);
      toast(`You already have all we’ve got of: ${names.join(', ')}.`);
      if (!capped.length) { openCart(btn); return; }
    }
    busy = true; notice(''); render();
    const label = btn ? btn.innerHTML : '';
    if (btn) btn.innerHTML = '<span>Adding…</span>';
    let ok = false;
    try {
      if (SHOP) {
        let r;
        try {
          r = await request(ROOT + 'cart/add.js', { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify({ items:capped }) });
        } catch (e) {
          await refreshCart().catch(() => {});
          notice('We couldn’t confirm that last add. Check your cart below before adding again.');
          return;
        }
        if (!r.ok) {
          await refreshCart().catch(() => {});
          notice((r.data && (r.data.description || r.data.message)) || 'We couldn’t add that to your cart.');
          return;
        }
        await refreshCart().catch(() => {});
      } else {
        const lines = readLocal();
        capped.forEach(i => { const l = lines.find(x => x.id === i.id); if (l) l.q += i.quantity; else lines.push({ id:i.id, q:i.quantity }); });
        writeLocal(lines); await refreshCart();
      }
      ok = true;
    } finally {
      busy = false;
      if (btn) {
        if (ok) {
          btn.classList.add('is-added'); btn.innerHTML = '<span>Added to cart</span>';
          setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = label; render(); }, 1600);
        } else btn.innerHTML = label;
      }
      render();
      openCart(btn);
    }
  }

  async function changeLine(key, qty) {
    if (busy) return;
    busy = true; notice(''); renderCart();
    try {
      if (SHOP) {
        const r = await request(ROOT + 'cart/change.js', { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify({ id:key, quantity:qty }) });
        if (r.ok && r.data && Array.isArray(r.data.items)) cart = fromShopify(r.data);
        else { notice((r.data && (r.data.description || r.data.message)) || 'We couldn’t update that quantity.'); await refreshCart().catch(() => {}); }
      } else {
        const lines = readLocal().map(l => String(l.id) === key ? { ...l, q:qty } : l).filter(l => l.q > 0);
        writeLocal(lines); cart = fromLocal(lines);
      }
    } catch (e) {
      notice('We couldn’t update your cart. Check your connection and try again.');
      await refreshCart().catch(() => {});
    } finally { busy = false; render(); }
  }

  function checkout() {
    if (!cart.count || busy) return;
    if (SHOP) { location.assign(ROOT + 'checkout'); return; }
    location.href = STORE + 'cart/' + readLocal().map(l => l.id + ':' + l.q).join(',');
  }

  function renderCart() {
    $$('[data-cart-count]').forEach(el => { el.textContent = cart.count; });
    $('[data-cart-sub]').textContent = cart.count ? money(cart.subtotal) : 'Cart';
    $('[data-subtotal]').textContent = money(cart.subtotal);
    const free = cart.subtotal >= FREE, need = FREE - cart.subtotal;
    const mt = $('[data-meter-text]');
    mt.textContent = !cart.count ? `Free shipping on orders over ${money(FREE)}` : free ? 'You’ve unlocked free shipping.' : `You’re ${money(need)} away from free shipping.`;
    mt.className = free && cart.count ? 'free' : '';
    $('[data-meter-bar]').style.width = Math.min(100, cart.subtotal / FREE * 100) + '%';
    const ann = $('[data-ann]');
    ann.innerHTML = !cart.count ? 'Free shipping on orders over <b>$120</b><span class="ann-x"> · Ships in 1–3 business days</span>'
      : free ? '<b>Free shipping unlocked</b><span class="ann-x"> · Ships in 1–3 business days</span>'
      : `You’re <b>${money(need)}</b> away from free shipping`;

    $('[data-lines]').innerHTML = cart.lines.map(l => `
      <li class="line">
        <div class="th">${l.image ? `<img src="${esc(l.image)}" alt="" width="52" height="52">` : ''}</div>
        <div><div class="nm">${esc(l.title)}</div>${l.variant ? `<div class="vr">${esc(l.variant)}</div>` : ''}
          <div class="qty" role="group" aria-label="Quantity for ${esc(l.title)}">
            <button type="button" data-q="${esc(l.key)}" data-to="${l.qty - 1}" aria-label="Decrease" ${busy ? 'disabled' : ''}>−</button>
            <span class="num">${l.qty}</span>
            <button type="button" data-q="${esc(l.key)}" data-to="${l.qty + 1}" aria-label="Increase" ${busy || (l.max !== null && l.qty >= l.max) ? 'disabled' : ''}>+</button>
          </div></div>
        <div><div class="lp num">${money(l.line)}</div><button type="button" class="rm" data-q="${esc(l.key)}" data-to="0" ${busy ? 'disabled' : ''}>Remove</button></div>
      </li>`).join('');
    $('[data-empty]').hidden = cart.count > 0;

    // upsells: setup pieces not yet in the cart, cheapest that clears free shipping first
    const inCart = new Set(cart.lines.map(l => byId.get(l.id)).filter(Boolean).map(o => o.k));
    let ups = ORDER.filter(k => !inCart.has(k) && avail(k));
    if (!free && cart.count) ups.sort((a, b) => (variant(b).price >= need) - (variant(a).price >= need) || variant(a).price - variant(b).price);
    $('[data-ups]').hidden = !ups.length;
    $('[data-ups] h3').textContent = cart.count ? 'Complete your setup' : 'Start your setup';
    $('[data-ups-list]').innerHTML = ups.slice(0, 4).map(k => {
      const v = variant(k), unlocks = cart.count && !free && v.price >= need;
      return `<li class="up"><img src="${imgOf(v)}" alt="" width="44" height="44">
        <div><b>${esc(P[k].name)}</b><small>${P[k].step} · ${unlocks ? 'unlocks free shipping' : money(v.price)}</small></div>
        <button type="button" data-upsell="${k}" ${busy ? 'disabled' : ''}>+ Add ${money(v.price)}</button></li>`;
    }).join('');
    const co = $('[data-checkout]');
    co.disabled = !cart.count || busy;
  }

  // ---------- drawer ----------
  const drawer = $('#cart');
  let lastFocus = null;
  drawer.inert = true; drawer.setAttribute('aria-hidden', 'true');
  function openCart(opener) {
    if (document.body.classList.contains('drawer-open')) return;
    lastFocus = opener || document.activeElement;
    document.body.classList.add('drawer-open');
    drawer.inert = false; drawer.removeAttribute('aria-hidden');
    setSticky();
    setTimeout(() => $('.dr-close', drawer).focus(), 40);
  }
  function closeCart() {
    if (!document.body.classList.contains('drawer-open')) return;
    document.body.classList.remove('drawer-open');
    drawer.inert = true; drawer.setAttribute('aria-hidden', 'true');
    setSticky();
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus();
  }
  document.addEventListener('keydown', e => {
    if (!document.body.classList.contains('drawer-open')) return;
    if (e.key === 'Escape') { closeCart(); return; }
    if (e.key !== 'Tab') return;
    const f = $$('a[href],button:not([disabled]),input:not([disabled])', drawer).filter(el => el.offsetParent !== null);
    if (!f.length) return;
    if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  });

  const toastEl = $('[data-toast]');
  let toastTimer;
  function toast(msg) { toastEl.textContent = msg; toastEl.classList.add('show'); clearTimeout(toastTimer); toastTimer = setTimeout(() => toastEl.classList.remove('show'), 4200); }
  function notice(msg) { const n = $('[data-cart-notice]'); n.textContent = msg; n.hidden = !msg; }

  // ---------- sticky bar + header ----------
  const sticky = $('[data-sticky]'), hdr = $('[data-hdr]');
  const watched = [$('[data-main-cta]'), $('.final .cta-xl'), $('#math .cta-xl')];
  const visible = new Set();
  function setSticky() {
    const show = !visible.size && !document.body.classList.contains('drawer-open');
    sticky.classList.toggle('show', show);
    sticky.inert = !show;
    if (show) sticky.removeAttribute('aria-hidden'); else sticky.setAttribute('aria-hidden', 'true');
  }
  function measure() {
    visible.clear();
    watched.forEach(el => { const r = el.getBoundingClientRect(), seen = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0); if (seen >= r.height * 0.6) visible.add(el); });
    hdr.classList.toggle('scrolled', scrollY > 8);
    setSticky();
  }
  // A CTA counts as visible only when most of it is on screen, so a sliver at the fold still gets the sticky bar.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => { entries.forEach(en => en.intersectionRatio >= 0.6 ? visible.add(en.target) : visible.delete(en.target)); setSticky(); }, { threshold:[0, 0.6, 1] });
    watched.forEach(el => io.observe(el));
  }
  addEventListener('scroll', () => { hdr.classList.toggle('scrolled', scrollY > 8); }, { passive:true });
  addEventListener('resize', measure, { passive:true });

  // ---------- events ----------
  document.addEventListener('change', e => {
    const t = e.target;
    if (t.matches('[data-opt]')) { const k = t.dataset.opt; if (P[k].v[+t.value]) { state.sel[k] = +t.value; render(); } }
    if (t.matches('[data-pick]')) { t.checked ? state.picks.add(t.dataset.pick) : state.picks.delete(t.dataset.pick); render(); }
  });
  document.addEventListener('click', e => {
    const t = e.target.closest('button,a');
    if (!t) return;
    if (t.matches('[data-retry-catalog]')) { loadCatalog(); return; }
    if (t.matches('[data-add]')) { addItems([{ id:variant(t.dataset.add).id, quantity:1 }], t); return; }
    if (t.matches('[data-add-picked]')) { addItems(picked().map(k => ({ id:variant(k).id, quantity:1 })), t); return; }
    if (t.matches('[data-add-setup]')) { addItems(SETUPS[t.dataset.addSetup].keys.filter(avail).map(k => ({ id:variant(k).id, quantity:1 })), t); return; }
    if (t.matches('[data-customize]')) { state.picks = new Set(SETUPS[t.dataset.customize].keys); render(); $('#build').scrollIntoView({ behavior:'smooth', block:'start' }); return; }
    if (t.matches('[data-pick-add]')) { state.picks.add(t.dataset.pickAdd); render(); return; }
    if (t.matches('[data-upsell]')) { addItems([{ id:variant(t.dataset.upsell).id, quantity:1 }], null); return; }
    if (t.matches('[data-open-cart]')) { openCart(t); return; }
    if (t.matches('[data-close-cart]')) { closeCart(); return; }
    if (t.matches('[data-q]')) { changeLine(t.dataset.q, Math.max(0, +t.dataset.to)); return; }
    if (t.matches('[data-checkout]')) { checkout(); return; }
  });
  $('.dr-scrim').addEventListener('click', closeCart);

  // ---------- boot ----------
  buildOpts();
  applyFeature();
  if (SHOP) ORDER.forEach(k => P[k].v.forEach(v => { if (stockOf(v) === 0) soldOut.add(v.id); }));
  settleVariants();
  render();
  measure();
  refreshCart().catch(() => notice('We couldn’t load your cart. Refresh to try again.'));
  loadCatalog();
  addEventListener('pageshow', e => { if (e.persisted) { refreshCart().catch(() => {}); loadCatalog(); } });
})();
